import React from "react";
import dayjs from "dayjs";
import {DeleteOutlined} from "@ant-design/icons";
import {Button, ButtonVariants, DatePicker, DatePickerSizes, Input, InputNumber, InputSizes, Select, SelectSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import {PAYSTUB_SOURCE_OPTIONS} from "../uploads-simulator.constants";
import styles from "../styles/sor-composer.module.scss";

/**
 * One authored paystub row. Period start/end are two `DatePicker`s (the
 * design-elements `RangePicker` binds a tuple; two pickers keep the stored
 * strings independent, matching the SOR schema). Extra keys the row carries
 * (e.g. `earnings` from a server preset) are preserved untouched by the
 * spread in `setField`.
 *
 * @param {Object} props
 * @param {Object} props.row
 * @param {number} props.index
 * @param {Record<string, string>} [props.errors] — keyed `recent_paystubs.<index>.<field>`
 * @param {(index: number, row: Object) => void} props.onChange
 * @param {(index: number) => void} props.onRemove
 */
function SorPaystubRow({row, index, errors, onChange, onRemove}) {
    const value = row || {};
    const setField = (field, fieldValue) => onChange(index, {...value, [field]: fieldValue});
    const errorAt = field => errors?.[`recent_paystubs.${index}.${field}`];
    const toDate = raw => (raw ? dayjs(raw) : null);

    return (
        <div className={styles.paystubRow}>
            <div className={styles.paystubRowHeader}>
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.paystubRowTitle}>
                    Paystub {index + 1}
                </Text>
                <Button variant={ButtonVariants.TEXT} className={styles.removeRowButton} aria-label={`Remove paystub ${index + 1}`} onClick={() => onRemove(index)}>
                    <DeleteOutlined />
                </Button>
            </div>

            <div className={styles.fieldGrid}>
                <DatePicker
                    label="Pay date"
                    size={DatePickerSizes.MEDIUM}
                    className={styles.dateField}
                    value={toDate(value.pay_date)}
                    onChange={date => setField("pay_date", date ? date.format("YYYY-MM-DD") : "")}
                />
                <DatePicker
                    label="Period start"
                    size={DatePickerSizes.MEDIUM}
                    className={styles.dateField}
                    value={toDate(value.pay_period_start)}
                    onChange={date => setField("pay_period_start", date ? date.format("YYYY-MM-DD") : "")}
                />
                <DatePicker
                    label="Period end"
                    size={DatePickerSizes.MEDIUM}
                    className={styles.dateField}
                    status={errorAt("pay_period_end") ? "error" : ""}
                    value={toDate(value.pay_period_end)}
                    onChange={date => setField("pay_period_end", date ? date.format("YYYY-MM-DD") : "")}
                />
                <InputNumber
                    label="Gross pay ($)"
                    size={InputSizes.MEDIUM}
                    className={styles.numberField}
                    min={0}
                    status={errorAt("gross_pay_amount") ? "error" : ""}
                    value={value.gross_pay_amount}
                    onChange={amount => setField("gross_pay_amount", amount)}
                />
                <InputNumber
                    label="Net pay ($)"
                    size={InputSizes.MEDIUM}
                    className={styles.numberField}
                    min={0}
                    status={errorAt("net_pay_amount") ? "error" : ""}
                    value={value.net_pay_amount}
                    onChange={amount => setField("net_pay_amount", amount)}
                />
                <InputNumber
                    label="Check amount ($)"
                    size={InputSizes.MEDIUM}
                    className={styles.numberField}
                    min={0}
                    status={errorAt("check_amount") ? "error" : ""}
                    value={value.check_amount}
                    onChange={amount => setField("check_amount", amount)}
                />
                <Input label="Employer name" size={InputSizes.MEDIUM} value={value.employer_name || ""} onChange={event => setField("employer_name", event.target.value)} />
                <Input label="Employee name" size={InputSizes.MEDIUM} value={value.employee_name || ""} onChange={event => setField("employee_name", event.target.value)} />
                <Select label="Source" size={SelectSizes.MEDIUM} options={PAYSTUB_SOURCE_OPTIONS} value={value.source || "real"} onChange={source => setField("source", source)} />
            </div>
            {errorAt("pay_period_end") ? (
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.fieldError}>
                    {errorAt("pay_period_end")}
                </Text>
            ) : null}
        </div>
    );
}

export default SorPaystubRow;
